import styled from "@emotion/styled"
import CurrencyBanner from "components/header/currency-banner/CurrencyBanner"
import { Icon } from "components/icon/Icon"
import Crown from "components/nav-v2/Crown"
import Overlay from "components/nav-v2/Overlay"
import Panes from "components/nav-v2/panes/Panes"
import Skip from "components/nav-v2/Skip"
import { cssVars, useNav } from "components/nav-v2/Wrapper"
import PushNotification from "components/push-notification/PushNotification"
import Wishlist from "components/wishlist/Toggle"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { useUser } from "contexts/User"
import getMediaQuery from "css/breakpoints"
import { buttonReset } from "css/buttons"
import { legend100, normal } from "css/text"
import { animate, motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion"
import useSSR from "hooks/useSSR"
import { useId, useLayoutEffect, useRef } from "react"

const Wrapper = styled.div`
  overflow: hidden;
  position: fixed;
  inset-block-start: calc(100px * (1 - round(up, var(${cssVars.main.panes.expanded}))));
  inset-block-end: calc(100px * (1 - round(up, var(${cssVars.main.panes.expanded}))));
  inset-inline: 0;
  z-index: 9998;
  pointer-events: none;
  display: grid;
  grid-template-rows: var(${cssVars.main.bar.height}) 1fr;

  // &[data-actions*="disappear"] {
  //   content-visibility: hidden;
  // }

  &[data-actions*="disappear"] {
    display: none !important;
  }

  & > * {
    grid-column: 1/1;
  }
`

const Placeholder = styled.div`
  height: var(${cssVars.main.bar.height});
  display: none;

  body:has(header[data-actions*="disappear"]) & {
    display: block !important;
  }
`

const Bar = styled.header`
  --touch-padding: 0.5rem;
  --inset-inline: 1.25rem;
  --inset-inline-desktop: 7.25rem;

  &[data-actions*="disappear"] {
    display: none !important;
  }

  [data-cfg="true"] & {
    display: grid !important;
    visibility: hidden;

    & [aria-live="polite"] {
      visibility: visible;
    }
  }

  --nav-main-bar-start: calc(var(${cssVars.main.alert.height}) * var(${cssVars.main.alert.expanded}));
  --nav-main-bar-offset: calc(var(--nav-main-bar-start) * clamp(0, 1 - var(${cssVars.main.bar.pinned}) + var(${cssVars.main.panes.expanded}), 1));
  position: sticky;
  inset-block-start: var(--nav-main-bar-start);
  inset-inline: 0;
  height: var(${cssVars.main.bar.height});
  z-index: 9999;
  background: rgb(from white r g b / 1);
  padding-inline: var(--inset-inline);
  padding-inline: var(--outer-margin);

  transform: translate3d(0, calc((1 - var(${cssVars.main.bar.pinned})) * -100% - var(--nav-main-bar-offset)), 0);
  content-visibility: visible;
  -webkit-font-smoothing: antialiased;

  &[data-actions*="lock"] {
    ${cssVars.main.bar.pinned}: var(${cssVars.main.lock})!important;
  }

  &[data-actions*="sub"] {
    z-index: 9995 !important; /* header should stay behind overlay on mobile when navigating with the keyboard */
  }

  ${getMediaQuery("m", { max: false })} {
    &[data-actions*="conceal"] {
      z-index: 9995; /* behinde overlay when primary pane is expanded, stay over for secondary */
    }

    --nav-main-bar-offset: calc(var(--nav-main-bar-start) * (1 - var(${cssVars.main.bar.pinned})));

    &[data-actions*="conceal"]:not([data-actions*="safe"]) {
      transform: translate3d(
        0,
        calc(
          clamp(0, 1 - var(${cssVars.main.bar.pinned}) + var(${cssVars.main.panes.expanded}), 1) * -100% - var(${cssVars.main.alert.height}) *
            var(${cssVars.main.panes.expanded})
        ),
        0
      );
    }

    &[data-actions*="hide"]:not([data-actions*="safe"]) {
      content-visibility: hidden;
    }
  }

  ${Wrapper}:focus-within & {
    ${cssVars.main.bar.pinned}: 1!important;
  }

  pointer-events: auto;
  & > * {
    pointer-events: none;
  }

  display: grid;
  & > * {
    grid-column: 1/1;
    grid-row: 1/1;
    display: flex;
  }

  & button,
  & a {
    pointer-events: auto;
  }

  & .crown {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  > ul {
    justify-content: space-between;
    align-content: center;
    align-items: center;
  }

  ${getMediaQuery("m", { max: true })} {
    /* Shared transition for both blocks */

    > ul > li:last-of-type > .tools {
      opacity: calc(1 - var(${cssVars.main.panes.expanded}));
    }

    > ul > li:last-of-type > .languages {
      content-visibility: hidden;
      pointer-events: none;
      opacity: min(var(${cssVars.main.panes.primary.expanded}), 1 - var(${cssVars.main.panes.primary.sub.languages.expanded}));
    }

    .crown {
      content-visibility: visible;
      opacity: 1;
    }

    &[data-actions*="opt-languages"] > ul > li:last-of-type > .tools {
      pointer-events: none;
    }

    &[data-actions*="opt-languages"][data-actions*="opt-hide"] > ul > li:last-of-type > .tools {
      content-visibility: hidden;
    }

    &[data-actions*="opt-languages"] .crown {
      opacity: calc(1 - var(${cssVars.main.panes.primary.expanded}));
      pointer-events: none;
    }

    &[data-actions*="opt-languages"][data-actions*="opt-hide"] .crown {
      content-visibility: hidden;
    }

    &[data-actions*="opt-languages"] > ul > li:last-of-type > .languages {
      content-visibility: visible;
      pointer-events: auto;
    }
  }

  > div:last-of-type {
    display: flex;
    justify-content: center;
  }
`

const Primary = styled.li`
  display: grid;
  align-items: center;
  margin-inline-start: -1rem;

  & > .back {
    grid-area: 1/1;
    padding: 1rem;
    display: grid;

    opacity: var(${cssVars.main.panes.primary.sub.languages.expanded});

    > button {
      ${buttonReset}
      padding:0.5rem;
      margin-top: 0.25rem;
      margin-inline-start: -0.5rem;
      position: relative;

      svg {
        position: absolute;
        inset-inline-start: 0.5rem;
        inset-block-start: 0;
        transform: scale(-1);
      }

      [dir="rtl"] & {
        svg {
          transform: scale(1);
        }
      }
    }
  }
`

const Secondary = styled.li`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-items: end;
  margin-inline-end: -0.5rem;

  > div {
    display: flex;
    flex-direction: row;
    grid-area: 1/1;

    ${getMediaQuery("m")} {
      gap: calc(4vw - var(--touch-padding) * 2);
    }
    ${getMediaQuery("xxl")} {
      margin-inline-end: 0;
    }
  }

  &[inert] {
    /* only for secondary pane on desktop */
    pointer-events: none;
    opacity: calc(1 - var(${cssVars.main.panes.secondary.expanded}));
  }
`

const Menu = styled.button`
  ${buttonReset}
  ${legend100}
  ${normal}

  grid-area: 1/1;
  opacity: calc(1 - var(${cssVars.main.panes.primary.sub.languages.expanded}));

  pointer-events: auto;
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 1ex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  transition: color 300ms;

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      color: rgb(var(--green));
    }
  }

  > span {
    display: none;
    opacity: 0;
    transform: scale(0);
    transition: opacity 300ms, transform 300ms;

    &[inert] {
      /* only for secondary pane on desktop */
      transition: none;
      pointer-events: none;
      opacity: calc(1 - var(${cssVars.main.panes.secondary.expanded}));
    }

    ${getMediaQuery("m")} {
      display: flex;
    }

    ${getMediaQuery("xxl", { min: true })} {
      opacity: min(var(--m, 0), 1);
      transform: scale((1 - min(var(--m, 0), 1)));
    }
  }

  > em {
    position: relative;
    display: grid;
    flex-direction: column;
    position: relative;
    width: fit-content;
  }
`

const Line = styled(motion.span)`
  width: 1.25rem;
  height: 2px;
  background: currentColor;
`

const Storelocator = styled.button`
  ${buttonReset}
  ${legend100}
  ${normal}

  &, & * {
    text-wrap: nowrap !important;
  }
  padding: 0.8rem 0.5rem;
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 1ex;
  flex-direction: row;
  align-items: center;
  transition: color 300ms;
  cursor: pointer;

  > svg {
    width: 15px;
    height: 15px;
    fill: currentColor;
  }

  ${getMediaQuery("m")} {
    > svg {
      width: inherit;
      height: inherit;
    }
  }

  > span {
    opacity: 0;
    transition: opacity 300ms, transform 300ms;
    width: 0;

    position: absolute;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    [data-actions*="opt-languages"] & {
      position: static;
    }

    ${getMediaQuery("m")} {
      display: flex;
      position: static;
      overflow: visible;
      clip: auto;
    }

    ${getMediaQuery("xxl", { min: true })} {
      opacity: min(var(--m, 0), 1);
      transform: scale((1 - min(var(--m, 0), 1)));
      width: min(var(--m, 0) * 100%, 100%);
    }
  }

  @media (any-hover: hover) and (any-pointer: fine) {
    &:hover {
      color: rgb(var(--green));
    }
  }

  &[data-active="true"] {
    pointer-events: none;
    color: rgb(var(--green));
  }
`
const Search = styled(Storelocator)`
  > svg {
    [dir="rtl"] & {
      transform: rotate(90deg);
    }
  }
`

const Languages = styled(Storelocator)`
  > span {
    ${legend100}
    display: block;
    opacity: 1;
    width: fit-content;
  }
  ${getMediaQuery("m")} {
    display: none;
  }
`

const NavMain = props => {
  const console = useConsole()
  const nav = useNav()
  const locale = useLocale()
  const { scrollY } = useScroll()
  const rmainbar = useRef(null)
  const rwrapper = useRef(null)
  const rburger = useRef(null)
  const rtools = useRef(null)
  const rsearch = useRef(null)
  const rlanguages = useRef(null)
  const rback = useRef(null)
  const dictionary = useDictionary()
  const navigation = useNavigation()
  const ssr = useSSR()
  const user = useUser()
  const uid = useId()

  let distance = 0
  useMotionValueEvent(scrollY, "change", latest => {
    const previous = scrollY.getPrevious()

    const delta = latest - previous
    const dir = delta === 0 ? 0 : delta > 0 ? 1 : -1
    const bar = rmainbar.current
    const actions = new Set((bar.dataset.actions ?? "").split(",").filter(Boolean))
    let update = false
    const refSafeHeight = nav.main.safe.motion.height.get()

    if (!actions.has("unpin") && dir > 0 && latest > refSafeHeight) {
      if (distance > refSafeHeight) {
        actions.add("unpin")
        actions.delete("pin")
        animate(nav.main.bar.motion.pinned, 0)
        update = true
        distance = 0
      } else {
        distance += delta
      }
    }

    if ((!actions.has("pin") && dir < 0) || latest <= refSafeHeight) {
      if (distance < -refSafeHeight || latest <= refSafeHeight) {
        actions.add("pin")
        actions.delete("unpin")
        animate(nav.main.bar.motion.pinned, 1, Object.assign({}, latest < 0 && { duration: 0 }))
        update = true
        distance = 0
      } else {
        distance += delta
      }
    }

    if (previous <= refSafeHeight && latest > refSafeHeight) {
      actions.delete("safe")
      update = true
    }

    if (previous > refSafeHeight && latest <= refSafeHeight) {
      actions.add("safe")
      update = true
    }

    if (update) {
      bar.dataset.actions = [...actions].join(",")
    }
  })

  useMotionValueEvent(nav.main.bar.motion.pinned, "change", latest => {
    const previous = nav.main.panes.primary.motion.expanded.getPrevious()

    if (Number.isInteger(latest) || Number.isInteger(previous)) {
      const actions = new Set((rmainbar.current.dataset.actions ?? "").split(",").filter(Boolean))

      if (latest === 0) {
        actions.add("disappear")
      } else {
        actions.delete("disappear")
      }

      rmainbar.current.dataset.actions = [...actions].join(",")
    }
  })

  useMotionValueEvent(nav.main.panes.motion.expanded, "change", latest => {
    const previous = nav.main.panes.primary.motion.expanded.getPrevious()
    if (Number.isInteger(latest) || Number.isInteger(previous)) {
      const actions = new Set((rwrapper.current.dataset.actions ?? "").split(",").filter(Boolean))
      if (latest === 0) {
        actions.add("disappear")
      } else {
        actions.delete("disappear")
      }
      rwrapper.current.dataset.actions = [...actions].join(",")
    }
  })

  useMotionValueEvent(nav.main.panes.primary.motion.expanded, "change", latest => {
    const previous = nav.main.panes.primary.motion.expanded.getPrevious()

    if (Number.isInteger(latest) || Number.isInteger(previous)) {
      const actions = new Set((rmainbar.current.dataset.actions ?? "").split(",").filter(Boolean))

      if (latest > 0) {
        actions.delete("opt-secondary")
        actions.add("opt-languages")
      } else {
        actions.add("opt-secondary")
        actions.delete("opt-languages")
      }

      if (latest === 1) {
        actions.add("opt-hide")
      } else {
        actions.delete("opt-hide")
      }

      rmainbar.current.dataset.actions = [...actions].join(",")
    }
  })

  useMotionValueEvent(nav.main.panes.primary.sub.languages.motion.expanded, "change", latest => {
    const previous = nav.main.panes.primary.sub.languages.motion.expanded.getPrevious()

    if (Number.isInteger(latest) || Number.isInteger(previous)) {
      const actions = new Set((rmainbar.current.dataset.actions ?? "").split(",").filter(Boolean))

      if (latest > 0) {
        actions.add("ctx-languages")
        rback.current.inert = false
        rburger.current.inert = true
        rlanguages.current.inert = true
      } else {
        actions.delete("ctx-languages")
        rback.current.inert = true
        rburger.current.inert = false
        rlanguages.current.inert = false
      }

      rmainbar.current.dataset.actions = [...actions].join(",")
    }
  })

  /** languages OR tools become inert when primary pane is expanded OR not */
  const onprimaryexpandedchange = latest => {
    const previous = nav.main.panes.primary.motion.expanded.getPrevious()

    const tools = rtools.current.querySelector(".tools")
    const languages = rtools.current.querySelector(".languages")

    if (!previous || !latest) {
      tools.inert = latest > 0
      languages.inert = latest === 0
    }
  }
  useMotionValueEvent(nav.main.panes.primary.motion.expanded, "change", onprimaryexpandedchange)
  useLayoutEffect(() => {
    onprimaryexpandedchange(nav.main.panes.primary.motion.expanded.get())
  })

  /** languages AND tools become inert when secondary pane IS expanded */
  const onsecondaryexpandedchange = latest => {
    const previous = nav.main.panes.secondary.motion.expanded.getPrevious()
    if (!previous || !latest) {
      rtools.current.inert = latest > 0

      if (latest > 0) {
        rburger.current.setAttribute("aria-label", `${dictionary.close()} ${dictionary.menu().toLowerCase()}`)
        rburger.current.querySelector(".burgerLabel").textContent = dictionary.close()
      } else {
        rburger.current.setAttribute("aria-label", dictionary.menu())
        rburger.current.querySelector(".burgerLabel").textContent = dictionary.menu()
      }
    }
  }
  useMotionValueEvent(nav.main.panes.secondary.motion.expanded, "change", onsecondaryexpandedchange)
  useLayoutEffect(() => {
    onsecondaryexpandedchange(nav.main.panes.secondary.motion.expanded.get())
  })

  useMotionValueEvent(nav.main.panes.primary.motion.expanded, "change", latest => {
    const previous = nav.main.panes.primary.motion.expanded.getPrevious()

    if (Number.isInteger(latest) || Number.isInteger(previous)) {
      const actions = new Set((rmainbar.current.dataset.actions ?? "").split(",").filter(Boolean))

      if (latest > 0) {
        actions.add("conceal")
        rburger.current.setAttribute("aria-expanded", true)
      } else {
        rburger.current.setAttribute("aria-expanded", false)
        actions.delete("conceal")
      }

      if (latest === 1) {
        actions.add("hide")
      } else {
        actions.delete("hide")
      }

      distance = 0
      rmainbar.current.dataset.actions = [...actions].join(",")
    }
  })

  useMotionValueEvent(nav.sub.pane.motion.expanded, "change", latest => {
    const previous = nav.sub.pane.motion.expanded.getPrevious()

    if (!previous || !latest) {
      const actions = new Set((rmainbar.current.dataset.actions ?? "").split(",").filter(Boolean))

      if (latest > 0) actions.add("sub")
      else actions.delete("sub")

      distance = 0
      rmainbar.current.dataset.actions = [...actions].join(",")
    }
  })

  useLayoutEffect(() => {
    const controller = new AbortController()

    nav.addEventListener(
      "main:lock",
      ({ t }) => {
        const actions = new Set((rmainbar.current.dataset.actions ?? "").split(",").filter(Boolean))
        actions.add("lock")
        rmainbar.current.dataset.actions = [...actions].join(",")
      },
      { signal: controller.signal }
    )

    nav.addEventListener(
      "main:unlock",
      () => {
        const actions = new Set((rmainbar.current.dataset.actions ?? "").split(",").filter(Boolean))
        actions.delete("lock")
        rmainbar.current.dataset.actions = [...actions].join(",")
      },
      { signal: controller.signal }
    )

    nav.addEventListener(
      "focus:trap:deactivate:primary",
      () => {
        setTimeout(() => {
          rburger.current.focus()
        }, 4)
      },
      { signal: controller.signal }
    )

    nav.addEventListener(
      "focus:trap:deactivate:secondary",
      () => {
        setTimeout(() => {
          rsearch.current.focus()
        }, 4)
      },
      { signal: controller.signal }
    )
    return () => controller.abort()
  })

  const expandPane = pane => () => {
    const event = new Event(`${pane}:toggle`)
    nav.__internals.dispatchEvent(event)
  }

  const expanded = nav.main.panes.motion.expanded
  const topY = useTransform(expanded, [0, 0.5, 1], [-2, 0, 1])
  const topRotate = useTransform(expanded, [0, 0.8, 1], [0, 0, -45])

  const bottomY = useTransform(expanded, [0, 0.5, 1], [2, 0, -1])
  const bottomRotate = useTransform(expanded, [0, 0.8, 1], [0, 0, 45])

  // const hrefWishlist = navigation.localize("/wishlist")
  const hrefStoreLoc = navigation.localize("/store-locator")
  const navigateStoreLoc = e => {
    e.preventDefault()
    navigation.push(hrefStoreLoc)
  }
  // const navigateWishlist = e => {
  //   e.preventDefault()
  //   navigation.push(hrefWishlist)
  // }

  const switchPaneToLanguages = () => {
    const event = new Event("toggle:languages")
    nav.__internals.dispatchEvent(event)
  }

  const switchback = () => {
    const event = new Event("toggle:languages:back")
    nav.__internals.dispatchEvent(event)
  }

  if (!user.displayCurrencyBanner) {
    nav.main.alert.motion.expanded.set(0)
  }

  console.verbose("Nav:Main(%o)", locale.current, props)
  return (
    <>
      <PushNotification {...props.push_notification?.[0]} page_type={props.page_type} page_slug={props.page_slug} />
      <Skip nofooter={props.nofooter} />
      {user.displayCurrencyBanner ? <CurrencyBanner /> : null}
      <Placeholder />
      <Bar ref={rmainbar} className='nav bar' data-actions='pin,safe,secondary'>
        <ul>
          <Primary className='primary'>
            <div ref={rback} className='back' inert='true'>
              <button aria-label={dictionary.back()} type='button' className='back' onClick={switchback}>
                <Icon type='arrowBack' />
              </button>
            </div>
            <Menu
              aria-controls={`menu-${uid}-wrapper`}
              aria-expanded='false'
              ref={rburger}
              className='burger'
              type='button'
              onClick={expandPane("primary")}
              style={{ "--m": ssr ? 0 : 1 }}
            >
              <em>
                <Line style={{ y: topY, rotate: topRotate }} />
                <Line style={{ y: bottomY, rotate: bottomRotate }} />
              </em>
              <span className='burgerLabel'>{dictionary.menu()}</span>
            </Menu>
          </Primary>
          <Secondary ref={rtools} className='secondary'>
            <div className='tools'>
              <Search ref={rsearch} type='button' onClick={expandPane("secondary")} style={{ "--m": ssr ? 0 : 1 }}>
                <Icon type='search' size='18' />
                <span>{dictionary.search()}</span>
              </Search>
              <Storelocator
                as='a'
                href={hrefStoreLoc}
                type='button'
                onClick={navigateStoreLoc}
                data-active={navigation.slug.includes("/store-locator") ? "true" : "false"}
                aria-current={navigation.slug.includes("/store-locator") ? "page" : undefined}
                aria-hidden={navigation.slug.includes("/store-locator") ? "true" : "false"}
                tabIndex={navigation.slug.includes("/store-locator") ? -1 : 0}
                style={{ "--m": ssr ? 0 : 1 }}
              >
                <Icon type='storelocator' size='19' />
                <span>{dictionary.storeLocator()}</span>
              </Storelocator>
              <Wishlist />
              {/* <Wishlist
                as={navigation.slug !== "/wishlist" ? "a" : "div"}
                type={navigation.slug !== "/wishlist" ? "a" : "div"}
                href={hrefWishlist}
                onClick={navigateWishlist}
                disabled={navigation.slug === "/wishlist"}
                ariaDisabled={navigation.slug === "/wishlist"}
                aria-hidden={navigation.slug === "/wishlist" ? "true" : "false"}
                tabIndex={navigation.slug === "/wishlist" ? -1 : 0}
                style={{ "--m": ssr ? 0 : 1 }}
              >
                <Icon type='wishlist' />
                <span>{dictionary.myWatches()}</span>
              </Wishlist> */}
            </div>
            <div ref={rlanguages} className='languages'>
              <Languages aria-label={dictionary.ariaSwitchLanguage()} onClick={switchPaneToLanguages}>
                <Icon type='language' />
                <span className='languageLabel'>{locale?.current.label}</span>
              </Languages>
            </div>
          </Secondary>
        </ul>
        <div className='crown'>
          <Crown />
        </div>
      </Bar>
      <Wrapper id={`menu-${uid}-wrapper`} aria-modal='true' role='dialog' aria-label={dictionary.menu()} ref={rwrapper} data-actions='disappear'>
        <Panes {...props} rmainbar={rmainbar} />
      </Wrapper>
      <Overlay />
    </>
  )
}

export default NavMain
